// DonutLabelingAiPanel.jsx — Task 2.2
//
// Shows Donut v9 + Gemini Flash 3 ensemble results side-by-side with per-field adopt buttons.
//
// Props:
//   ensemble: {
//     donut        : object | null,
//     gemini       : object | null,
//     consensus    : { [field]: { donut, gemini, agree: bool } },
//     donut_error  : string | null,
//     gemini_error : string | null,
//     donut_ms     : number | null,
//     gemini_ms    : number | null,
//   } | null
//   onAdopt : (field: string, value: string) => void

import React, { useState } from "react";

// Fields to display in the panel (same order as FieldGrid, minus doc_type)
const PANEL_FIELDS = [
  "plate_no",
  "vin",
  "car_name",
  "year",
  "maker",
  "owner_name",
  "owner_id_no",
  "owner_address",
  "mileage_km",
  "displacement_cc",
  "vehicle_weight_kg",
  "length_mm",
  "width_mm",
  "height_mm",
  "canceled_date",
  "bid_amount_krw",
  "cbm",
];

function fmtMs(ms) {
  if (ms == null) return "-";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function truncate(val, max = 28) {
  if (!val) return "";
  const s = String(val);
  return s.length > max ? s.slice(0, max) + "…" : s;
}

export default function DonutLabelingAiPanel({
  ensemble,
  ensembleLoading,
  onAdopt,
  onReExtract,
  highlightDiff = true, // false → skip orange row background for Donut↔Gemini disagreements
}) {
  // Track recently-adopted cells for visual feedback (field → "D" | "G" | null)
  const [adopted, setAdopted] = useState({});

  const handleAdopt = (field, value, source) => {
    onAdopt(field, value);
    setAdopted((prev) => ({ ...prev, [field]: source }));
    setTimeout(() => setAdopted((prev) => ({ ...prev, [field]: null })), 1200);
  };
  // Loading placeholder
  if (ensembleLoading) {
    return (
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          padding: "12px 16px",
          marginTop: 12,
          background: "#f9fafb",
          color: "#9ca3af",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 12,
            height: 12,
            border: "2px solid #d1d5db",
            borderTopColor: "#2563eb",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        AI 추출 중...
      </div>
    );
  }

  if (!ensemble) {
    return (
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          padding: "12px 16px",
          marginTop: 12,
          background: "#f9fafb",
          color: "#9ca3af",
          fontSize: 12,
        }}
      >
        AI 추출 대기중...
      </div>
    );
  }

  const bothError = ensemble.donut_error && ensemble.gemini_error;
  const consensus = ensemble.consensus || {};

  // Build ordered rows — PANEL_FIELDS order, then any extra keys from consensus
  const extraKeys = Object.keys(consensus).filter(
    (k) => !PANEL_FIELDS.includes(k) && k !== "doc_type",
  );
  const displayFields = [...PANEL_FIELDS, ...extraKeys];

  // Only rows that have at least one non-empty value
  const rows = displayFields
    .map((field) => {
      const c = consensus[field] || { donut: null, gemini: null, agree: true };
      return {
        field,
        donut: c.donut || null,
        gemini: c.gemini || null,
        agree: c.agree,
      };
    })
    .filter((r) => r.donut || r.gemini);

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        marginTop: 12,
        overflow: "hidden",
        fontSize: 12,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: "#f9fafb",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <span style={{ fontWeight: 600, color: "#111", fontSize: 12 }}>
          AI 추출 (Donut v9 + Gemini Flash 3)
        </span>
        <span style={{ color: "#9ca3af", fontSize: 11 }}>
          Donut {fmtMs(ensemble.donut_ms)} · Gemini {fmtMs(ensemble.gemini_ms)}
        </span>
        {onReExtract && (
          <button
            onClick={onReExtract}
            style={{
              marginLeft: "auto",
              fontSize: 11,
              padding: "2px 10px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: "#fff",
              cursor: "pointer",
              color: "#374151",
            }}
          >
            AI 다시 추출
          </button>
        )}
      </div>

      {/* Both-error banner */}
      {bothError && (
        <div
          style={{
            padding: "8px 12px",
            background: "#fef2f2",
            color: "#dc2626",
            borderBottom: "1px solid #fecaca",
            fontSize: 12,
          }}
        >
          Donut 오류: {ensemble.donut_error}
          {ensemble.donut_error !== ensemble.gemini_error && (
            <> · Gemini 오류: {ensemble.gemini_error}</>
          )}
        </div>
      )}

      {/* Partial-error notices */}
      {!bothError && ensemble.donut_error && (
        <div
          style={{
            padding: "4px 12px",
            background: "#fff7ed",
            color: "#c2410c",
            fontSize: 11,
            borderBottom: "1px solid #fed7aa",
          }}
        >
          Donut 오류: {ensemble.donut_error}
        </div>
      )}
      {!bothError && ensemble.gemini_error && (
        <div
          style={{
            padding: "4px 12px",
            background: "#fff7ed",
            color: "#c2410c",
            fontSize: 11,
            borderBottom: "1px solid #fed7aa",
          }}
        >
          Gemini 오류: {ensemble.gemini_error}
        </div>
      )}

      {/* Table */}
      {rows.length === 0 ? (
        <div style={{ padding: "12px 16px", color: "#9ca3af" }}>
          추출된 필드 없음
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  background: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#6b7280",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                  }}
                >
                  field
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#6b7280",
                    fontSize: 11,
                  }}
                >
                  Donut
                </th>
                <th
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#6b7280",
                    fontSize: 11,
                  }}
                >
                  Gemini
                </th>
                <th
                  style={{
                    padding: "5px 8px",
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#6b7280",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                  }}
                >
                  일치
                </th>
                <th
                  style={{
                    padding: "5px 8px",
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#6b7280",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                  }}
                >
                  채택
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ field, donut, gemini, agree }) => (
                <tr
                  key={field}
                  style={{
                    background:
                      !agree && highlightDiff ? "#fff4e6" : "transparent",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <td
                    style={{
                      padding: "4px 10px",
                      fontFamily: "monospace",
                      color: "#374151",
                      fontSize: 11,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {field}
                  </td>
                  <td
                    style={{
                      padding: "4px 10px",
                      color: donut ? "#111827" : "#d1d5db",
                      fontSize: 11,
                      maxWidth: 160,
                    }}
                    title={donut || ""}
                  >
                    {donut ? truncate(donut) : "-"}
                  </td>
                  <td
                    style={{
                      padding: "4px 10px",
                      color: gemini ? "#111827" : "#d1d5db",
                      fontSize: 11,
                      maxWidth: 160,
                    }}
                    title={gemini || ""}
                  >
                    {gemini ? truncate(gemini) : "-"}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "center" }}>
                    {agree ? (
                      <span style={{ color: "#16a34a", fontSize: 12 }}>✓</span>
                    ) : (
                      <span style={{ fontSize: 12 }}>⚠</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "4px 8px",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {donut && (
                      <button
                        onClick={() => handleAdopt(field, donut, "D")}
                        title={`Donut: ${donut}`}
                        style={{
                          fontSize: 10,
                          padding: "1px 6px",
                          borderRadius: 3,
                          border: `1px solid ${adopted[field] === "D" ? "#1d4ed8" : "#bfdbfe"}`,
                          background:
                            adopted[field] === "D" ? "#1d4ed8" : "#eff6ff",
                          color: adopted[field] === "D" ? "#fff" : "#1d4ed8",
                          cursor: "pointer",
                          marginRight: 3,
                          fontWeight: 600,
                          transition: "background 0.15s, color 0.15s",
                        }}
                      >
                        D
                      </button>
                    )}
                    {gemini && (
                      <button
                        onClick={() => handleAdopt(field, gemini, "G")}
                        title={`Gemini: ${gemini}`}
                        style={{
                          fontSize: 10,
                          padding: "1px 6px",
                          borderRadius: 3,
                          border: `1px solid ${adopted[field] === "G" ? "#15803d" : "#bbf7d0"}`,
                          background:
                            adopted[field] === "G" ? "#15803d" : "#f0fdf4",
                          color: adopted[field] === "G" ? "#fff" : "#15803d",
                          cursor: "pointer",
                          fontWeight: 600,
                          transition: "background 0.15s, color 0.15s",
                        }}
                      >
                        G
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
